import React, { useEffect, useState } from 'react';
import styles from './payments-table.module.scss';
import { BusinessApi } from 'shared/api';
import { IPaymentHistoryItem } from 'shared/api/business/interfaces/payment-history-item.interface';
import { DefaultButton, RoundTriangle } from 'shared/ui';
import { RefundConfirmModal } from './refund-confirm-modal';

export const PaymentsTable: React.FC = () => {
  const [payments, setPayments] = useState<IPaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    BusinessApi.getPayments()
      .then((data) => setPayments(data))
      .catch(() => {
        // ошибка загрузки платежей — таблица покажет пустое состояние
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const handleRefundSuccess = () => {
    setSelectedPaymentId(null);
    setRefreshKey((k) => k + 1);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (item: IPaymentHistoryItem) => {
    if (item.isRefunded) return 'Возвращено';
    switch (item.status) {
      case 'succeeded':
        return 'Оплачено';
      case 'canceled':
        return 'Отменено';
      case 'pending':
        return 'Ожидает';
      default:
        return item.status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'succeeded':
        return styles.statusSuccess;
      case 'canceled':
        return styles.statusCanceled;
      case 'pending':
        return styles.statusPending;
      case 'refunded':
        return styles.statusRefunded;
      default:
        return '';
    }
  };

  const toggleExpand = (paymentId: string) => {
    setExpandedId((prev) => (prev === paymentId ? null : paymentId));
  };

  return (
    <div className={styles.table__container}>
      <h3 className={styles.table__title}>История платежей</h3>
      {loading ? (
        <div className={styles.table__loading}>Загрузка...</div>
      ) : payments.length === 0 ? (
        <div className={styles.table__empty}>Нет платежей</div>
      ) : (
        <>
          <div className={styles.table__wrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Сумма</th>
                  <th>Сотрудников</th>
                  <th>Месяцев</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const isExpandable = p.operations.length > 1;
                  return (
                    <React.Fragment key={p.paymentId}>
                      <tr
                        className={
                          isExpandable
                            ? styles.rowClickable
                            : styles.rowDisabled
                        }
                        onClick={
                          isExpandable
                            ? () => toggleExpand(p.paymentId)
                            : undefined
                        }
                      >
                        <td>
                          {isExpandable && (
                            <span className={styles.dateTriangle}>
                              <RoundTriangle
                                isRotated={expandedId === p.paymentId}
                              />
                            </span>
                          )}
                          {formatDate(p.dateTime)}
                        </td>
                        <td>{p.amount} ₽</td>
                        <td>{p.licCountEmployees}</td>
                        <td>{p.licExpireMonths}</td>
                        <td>
                          <span className={getStatusClass(p.status)}>
                            {getStatusLabel(p)}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {p.canRefund && (
                            <DefaultButton
                              buttonText="Вернуть"
                              onClick={() => setSelectedPaymentId(p.paymentId)}
                              color="gray"
                              className={styles.refundButton}
                            />
                          )}
                        </td>
                      </tr>
                      {expandedId === p.paymentId && (
                        <tr className={styles.expandedRow}>
                          <td colSpan={6}>
                            <div className={styles.operationsList}>
                              <div className={styles.operationsTitle}>
                                История операций
                              </div>
                              {p.operations.map((op, idx) => (
                                <div key={idx} className={styles.operationItem}>
                                  <span
                                    className={`${styles.operationDot} ${getStatusClass(op.status)}`}
                                  />
                                  <span className={styles.operationStatus}>
                                    {op.status === 'succeeded'
                                      ? 'Оплачено'
                                      : op.status === 'canceled'
                                        ? 'Отменено'
                                        : op.status === 'pending'
                                          ? 'Ожидает'
                                          : op.status === 'refunded'
                                            ? 'Возвращено'
                                            : op.status}
                                  </span>
                                  <span className={styles.operationDate}>
                                    {formatDateTime(op.dateTime)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.card}>
            {payments.map((p) => {
              const isExpandable = p.operations.length > 1;
              return (
                <div key={p.paymentId}>
                  <div
                    className={`${styles.card__item} ${isExpandable ? styles.rowClickable : styles.cardDisabled}`}
                    onClick={
                      isExpandable ? () => toggleExpand(p.paymentId) : undefined
                    }
                  >
                    <div className={styles.card__row}>
                      <span className={styles.card__label}>Дата</span>
                      <span className={styles.card__value}>
                        {isExpandable && (
                          <span className={styles.dateTriangle}>
                            <RoundTriangle
                              isRotated={expandedId === p.paymentId}
                            />
                          </span>
                        )}
                        {formatDate(p.dateTime)}
                      </span>
                    </div>
                    <div className={styles.card__row}>
                      <span className={styles.card__label}>Сумма</span>
                      <span className={styles.card__value}>{p.amount} ₽</span>
                    </div>
                    <div className={styles.card__row}>
                      <span className={styles.card__label}>Статус</span>
                      <span
                        className={`${styles.card__value} ${getStatusClass(p.status)}`}
                      >
                        {getStatusLabel(p)}
                      </span>
                    </div>
                    {p.canRefund && (
                      <div
                        className={styles.card__actions}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DefaultButton
                          buttonText="Вернуть"
                          onClick={() => setSelectedPaymentId(p.paymentId)}
                          color="red"
                          className={styles.refundButton}
                        />
                      </div>
                    )}
                  </div>
                  {expandedId === p.paymentId && (
                    <div className={styles.cardOperations}>
                      <div className={styles.operationsTitle}>
                        История операций
                      </div>
                      {p.operations.map((op, idx) => (
                        <div key={idx} className={styles.operationItem}>
                          <span
                            className={`${styles.operationDot} ${getStatusClass(op.status)}`}
                          />
                          <span className={styles.operationStatus}>
                            {op.status === 'succeeded'
                              ? 'Оплачено'
                              : op.status === 'canceled'
                                ? 'Отменено'
                                : op.status === 'pending'
                                  ? 'Ожидает'
                                  : op.status === 'refunded'
                                    ? 'Возвращено'
                                    : op.status}
                          </span>
                          <span className={styles.operationDate}>
                            {formatDateTime(op.dateTime)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {selectedPaymentId && (
        <RefundConfirmModal
          paymentId={selectedPaymentId}
          onClose={() => setSelectedPaymentId(null)}
          onSuccess={handleRefundSuccess}
        />
      )}
    </div>
  );
};
